import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import type { MasterExportScope } from '../../models/masterExport.types';

interface MasterExportMenuProps {
  visible: boolean;
  /** What the dialog is exporting, for its title: "Organization", "Entity". */
  title: string;
  /** Rows the current view covers, and rows in total. */
  viewCount: number;
  totalCount: number;
  busy?: boolean;
  onHide: () => void;
  onExport: (scope: MasterExportScope) => void;
  /** An extra class on the dialog, for a screen styling its own (Purchase Requests' design21 look). */
  className?: string;
  /** `list`: the four choices in one column, each closing on an upload glyph (Purchase Requests); `grid` by default. */
  layout?: 'grid' | 'list';
  /** The line under the title; the masters' sentence when absent. */
  subtitle?: string;
  /** A footer with a Cancel button that closes the dialog (Supplier and Purchase Requests, 2026-10-23). */
  showCancel?: boolean;
}

interface Choice {
  scope: MasterExportScope;
  icon: string;
  label: string;
  sub: (view: number, total: number) => string;
}

/*
  The four the reference offers. "Current view" carries the visible columns and
  the rows the filters leave; "all records" carries every column and ignores
  the column selection entirely.
*/
const CHOICES: Choice[] = [
  {
    scope: { rows: 'view', format: 'xlsx' },
    icon: 'pi-table',
    label: 'Current view · Excel',
    sub: (view) => `${String(view)} rows, visible columns`,
  },
  {
    scope: { rows: 'view', format: 'csv' },
    icon: 'pi-table',
    label: 'Current view · CSV',
    sub: (view) => `${String(view)} rows, visible columns`,
  },
  {
    scope: { rows: 'all', format: 'xlsx' },
    icon: 'pi-database',
    label: 'All records · Excel',
    sub: (_view, total) => `${String(total)} rows, all columns`,
  },
  {
    scope: { rows: 'all', format: 'csv' },
    icon: 'pi-database',
    label: 'All records · CSV',
    sub: (_view, total) => `${String(total)} rows, all columns`,
  },
];

/**
 * Export, in its four scopes.
 *
 * Each choice states the rows it would produce, because the difference between
 * "current view" and "all records" is invisible otherwise -- and exporting the
 * wrong one is only discovered after the file opens. The reference lays the
 * four out two by two rather than as a single column, so the pair that differ
 * only by format sit side by side.
 */
export function MasterExportMenu({
  visible,
  title,
  viewCount,
  totalCount,
  busy = false,
  onHide,
  onExport,
  className,
  layout = 'grid',
  subtitle = 'Choose the rows and columns to include in the exported file.',
  showCancel = false,
}: MasterExportMenuProps) {
  return (
    <Dialog
      /* Scoped backdrop: the mask is portalled to <body>, so no ancestor can reach it. */
      maskClassName="ms-mask"
      visible={visible}
      className={`ms-dialog ms-export-dialog${className ? ` ${className}` : ''}`}
      modal
      draggable={false}
      resizable={false}
      onHide={onHide}
      header={
        <div className="ms-pop__head">
          <span className="ms-pop__icon" aria-hidden="true">
            <i className="pi pi-download" />
          </span>
          <span className="ms-pop__title">Export {title}</span>
        </div>
      }
      footer={
        showCancel ? (
          <div className="ms-export__footer">
            <Button type="button" outlined className="ms-pop__action" label="Cancel" onClick={onHide} />
          </div>
        ) : undefined
      }
    >
      <p className="ms-pop__sub">{subtitle}</p>

      <div className={`ms-picks ms-picks--${layout}`}>
        {CHOICES.map((choice) => (
          <button
            key={choice.label}
            type="button"
            className="ms-pick"
            disabled={busy}
            onClick={() => {
              onExport(choice.scope);
            }}
          >
            <i className={`pi ${choice.icon} ms-pick__glyph`} aria-hidden="true" />
            <span className="ms-pick__text">
              <span className="ms-pick__title">{choice.label}</span>
              <span className="ms-pick__sub">{choice.sub(viewCount, totalCount)}</span>
            </span>
            {layout === 'list' ? <i className="pi pi-upload ms-pick__go" aria-hidden="true" /> : null}
          </button>
        ))}
      </div>
    </Dialog>
  );
}
